import Switch from '@components/switch/Switch';
import { useViewport } from '@lib/tools/hooks/useViewport';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import styles from '../Calculator.module.scss';
import { CheckBox } from '../checkbox/CheckBox';
import { DropDownCalc } from '../dropdown/Dropdown';
import CalculatorSlider from '../new-calculator-slider/NewCalculatorSlider';

// Вычисляет процент первоначального взноса от стоимости недвижимости
export const getInitPercent = f => {
  // Если стоимость ещё не задана — берём дефолтный пресет (например 30%)
  if (!f.realtyCost) return INITIAL_PAYMENT_PRESETS[2];
  const v = (f.initialPayment * 100 / f.realtyCost).toFixed(1);
  return Number(v);
};
export const INITIAL_PAYMENT_PRESETS = [15, 20, 30, 50];

// Пересчёт при изменении СТОИМОСТИ недвижимости
// Пользователь ПВ не трогал → сохраняем выбранный процент
export const recalcFromRealtyCost = (newRealtyCost, prev, percentRef) => {
  const realtyCost = newRealtyCost;
  // Используем СОХРАНЕННЫЙ процент, читая его напрямую из Рефа
  const percentToUse = percentRef.current;
  // Пересчитываем абсолютную сумму первоначального взноса
  const newInitialPaymentValue = Math.round(realtyCost * percentToUse / 100);
  const initialPayment = newInitialPaymentValue;

  // Сумма кредита — остаток от стоимости
  const creditAmount = realtyCost - initialPayment;
  return {
    ...prev,
    realtyCost,
    initialPayment,
    creditAmount
  };
};

// Пересчёт при изменении СУММЫ первоначального взноса
// Пользователь задал ПВ → фиксируем новый процент
export const recalcFromInitialPayment = (newInitialPayment, prev, percentRef) => {
  const realtyCost = prev.realtyCost;
  const initialPayment = Math.min(newInitialPayment, realtyCost);
  const creditAmount = realtyCost - initialPayment;

  // Сохраняем выбранный процент
  // Он будет использован при будущих изменениях realtyCost
  percentRef.current = getInitPercent({
    realtyCost,
    initialPayment
  });
  return {
    ...prev,
    initialPayment,
    creditAmount
  };
};

// Пересчёт при изменении СУММЫ кредита
// Пользователь оперирует "обратным" значением → ПВ вычисляется автоматически
export const recalcFromCreditAmount = (newCreditAmount, prev, percentRef) => {
  const realtyCost = prev.realtyCost;
  const creditAmount = Math.min(newCreditAmount, realtyCost);
  const initialPayment = realtyCost - creditAmount;

  // Фиксируем процент, получившийся из пользовательского ввода
  percentRef.current = getInitPercent({
    realtyCost,
    initialPayment
  });
  return {
    ...prev,
    creditAmount,
    initialPayment
  };
};
const getAverageRealPrice = flatsArray => {
  if (!Array.isArray(flatsArray) || flatsArray.length === 0) return 0;
  let min = null;
  let max = null;
  for (const f of flatsArray) {
    const p = Number(f?.real_price);
    if (!Number.isFinite(p)) continue;
    min = min === null ? p : Math.min(min, p);
    max = max === null ? p : Math.max(max, p);
  }
  if (min === null || max === null) return 0;
  return Math.floor((min + max) / 2);
};
const getPriceForMortgageCalc = (flat, flatsArray, fallbackAveragePrice) => {
  if (flat?.real_price) return flat.real_price;
  return getAverageRealPrice(flatsArray) || fallbackAveragePrice;
};
export const Filters = ({
  flat,
  filters,
  setFilters,
  ranges,
  programTypes,
  onSelectProgram,
  onOpenForm,
  isLoading
}) => {
  const flats = useSelector(state => state?.filters?.flatsData);
  const flatsArray = flats?.flats || [];
  const preferredPercentRef = useRef(getInitPercent(filters));
  useEffect(() => {
    preferredPercentRef.current = getInitPercent(filters);
  }, [filters.realtyCost, filters.initialPayment]);
  const {
    isMobile
  } = useViewport();
  useEffect(() => {
    if (flatsArray.length > 0) {
      const averagePrice = Math.floor((flats.price.min + flats.price.max) / 2);
      const price = getPriceForMortgageCalc(flat, flatsArray, averagePrice);
      setFilters(prev => {
        const realtyCost = price;
        const initialPayment = Math.round(realtyCost * INITIAL_PAYMENT_PRESETS[2] / 100);
        const creditAmount = Math.floor(realtyCost - initialPayment);
        return {
          ...prev,
          realtyCost,
          creditAmount,
          initialPayment
        };
      });
    }
  }, [flats?.price, flatsArray.length, flat]);
  const onChange = useCallback(name => value => {
    setFilters(prev_0 => {
      if (name === 'realtyCost') {
        return recalcFromRealtyCost(value, prev_0, preferredPercentRef);
      }
      if (name === 'initialPayment') {
        return recalcFromInitialPayment(value, prev_0, preferredPercentRef);
      }
      if (name === 'creditAmount') {
        return recalcFromCreditAmount(value, prev_0, preferredPercentRef);
      }

      // остальные поля оставляем как есть
      return {
        ...prev_0,
        [name]: value
      };
    });
  }, []);
  const onApplyInitialPercent = percent => {
    const percentClamped = Math.min(Math.max(percent, 10), 100);
    preferredPercentRef.current = percentClamped;
    const initialPayment_0 = Math.round(filters.realtyCost * percentClamped / 100);
    setFilters(prev_1 => ({
      ...prev_1,
      initialPayment: initialPayment_0,
      creditAmount: prev_1.realtyCost - initialPayment_0
    }));
  };
  const periodButtons = useMemo(() => {
    const max = ranges.period?.max;
    if (!max) return null;
    const buttons = [];
    for (let value_0 = 10; value_0 <= max; value_0 += 5) {
      buttons.push(value_0);
    }
    return <div className={styles.hintFilterBtns}>
        {buttons.map(val => <button key={val} type='button' className={cn(styles.btn, val === filters.period && styles.active)} onClick={() => onChange('period')(val)}>
            {val} лет
          </button>)}
      </div>;
  }, [ranges.period?.max, filters.period, onChange]);
  const initialPaymentButtons = useMemo(() => {
    const curInitPercent = filters.realtyCost > 0 ? Number((filters.initialPayment * 100 / filters.realtyCost).toFixed(1)) : 0;
    return <div className={styles.hintFilterBtns}>
        {INITIAL_PAYMENT_PRESETS.map(val_0 => <button key={val_0} type='button' className={cn(styles.btn, val_0.toFixed(0) === curInitPercent.toFixed(0) ? styles.active : '')} onClick={() => onApplyInitialPercent(val_0)}>
            {val_0.toFixed(0)}%
          </button>)}
      </div>;
  }, [filters.initialPayment, onChange]);
  const isFiltersVisible = ranges?.realtyCost?.min !== 0 || ranges?.realtyCost?.max !== 0;
  return <div className={styles.calc_block}>
      {isMobile && <p className={styles.calc_title}>Калькулятор ипотеки</p>}
      <DropDownCalc label='Выберите программу' list={programTypes} onSelect={onSelectProgram} />
      {isFiltersVisible && <div className={styles.calc_main}>
          <CalculatorSlider incomingValue={filters.realtyCost} label='Стоимость, млн ₽' onChange={onChange('realtyCost')} sliderValue={filters.realtyCost} maxSliderValue={ranges?.realtyCost?.max || 0} minSliderValue={ranges?.realtyCost?.min || 0} testId='price' />
          <div>
            <CalculatorSlider label='Первоначальный взнос, млн ₽' incomingValue={filters.initialPayment} onChange={onChange('initialPayment')} sliderValue={filters.initialPayment} minSliderValue={ranges?.initialPayment?.min || 0} maxSliderValue={isLoading ? filters.realtyCost * 0.999 : ranges?.initialPayment?.max || 0} initialPaymentPercentBase={filters.realtyCost} percent={100} testId='firstPayment' readOnly={ranges?.initialPayment?.min === 0 && ranges?.initialPayment?.max === 0} />
            {initialPaymentButtons}
          </div>

          <div>
            <CalculatorSlider label='Срок кредита, лет' incomingValue={filters.period} onChange={onChange('period')} minSliderValue={ranges.period?.min} maxSliderValue={ranges.period?.max} sliderValue={filters.period} testId='loanTerm' />
            {periodButtons}
          </div>

          <CalculatorSlider label='Возраст заёмщика, лет' incomingValue={filters.age} onChange={onChange('age')} minSliderValue={ranges.age?.min} maxSliderValue={ranges.age?.max} sliderValue={filters.age} testId='age' />
        </div>}
      <div className={styles.insurance}>
        <CheckBox id='insurance-checkbox' onChange={e => setFilters(prev_2 => ({
        ...prev_2,
        personalInsurance: Number(e.target.checked)
      }))} checked={!!filters.personalInsurance} />
        <p>Страхование жизни</p>
        <span>Оформляем полис онлайн в процессе покупки. Без страхования ставка будет выше.</span>
      </div>

      <div className={styles.sum_credit_block}>
        <span>Сумма кредита, млн ₽</span>
        <div className={styles.sum_credit} data-testid='mortgageValue'>
          {filters.creditAmount.toLocaleString('ru')}
        </div>
      </div>

      <div className={styles.bank_list__result}>
        <Switch handleSwitch={onOpenForm} switchText='ОСТАВИТЬ ЗАЯВКУ' black someStyles={styles.bank_list__callButton} testId='mortgageFormOpen' />

        <p className={styles.bank_list__notification}>
          * Приведенные расчеты носят предварительный характер. Окончательный расчет суммы кредита и размер ежемесячного
          платежа производятся банком после предоставления полного комплекта документов и проведения оценки
          платежеспособности клиента.
        </p>
      </div>
    </div>;
};